import {
  useModal, Typography, Spacing, SpacingName, Symbol, useNotifications, ICON_CASH_MULTIPLE, ICON_MAP_MARKER,
} from '@doveit/bricks';
import React, {
  FunctionComponent, useCallback, useMemo, useState,
} from 'react';
import { formatEuroRange, formatSquareMetersRange } from '@doveit/hammer';
import { useRouter } from 'next/router';
import { OptInV2, SourceType, WebSearchCriteria } from '../../../services/dtos';
import ContactFormV3, { ContactFormModelV3 } from '../../components/contact-form-V3/ContactFormV3';
import { RULER } from '../../themes/icons';
import { OptInType } from '../../../domain/optInType';
import { createWebSearchCriteria } from '../../../services/web-search-criteria/createWebSearchCriteria';
import * as styles from './CreateWebSearchCriteria.style';
import { mapTypeToLabel } from '../../utils/place/constants';
import getTrafficSource from '../../utils/traffic-source/getTrafficSource';

export interface CreateWebSearchCriteriaProps {
  initialValues?: Partial<WebSearchCriteria>,
  dataRef?: string,
  onSuccess?: (webSearchCriteria: WebSearchCriteria) => void,
}

const ICONS = {
  place: ICON_MAP_MARKER,
  size: RULER,
  price: ICON_CASH_MULTIPLE,
};

export const CREATE_WEB_SEARCH_CRITERIA_SUCCESS_MESSAGE = 'La tua ricerca è stata salvata correttamente';
export const CREATE_WEB_SEARCH_CRITERIA_ERROR_MESSAGE = 'Si è verificato un errore durante il salvataggio della ricerca';

const CreateWebSearchCriteria: FunctionComponent<CreateWebSearchCriteriaProps> = ({
  initialValues,
  dataRef = 'save-search',
  onSuccess,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const { addSuccess, addError } = useNotifications();
  const saveSearchModal = useModal();
  const router = useRouter();

  const initialFormValues: ContactFormModelV3 = useMemo(() => ({
    email: initialValues?.email ?? '',
    name: initialValues?.name ?? '',
    phoneNumber: initialValues?.phoneNumber ?? '',
    marketing: initialValues?.optIns?.some((optIn) => optIn.type === OptInType.MARKETING) ?? false,
    thirdParties: initialValues?.optIns?.some((optIn) => optIn.type === OptInType.THIRD_PARTIES) ?? false,
  }), [initialValues?.email, initialValues?.name, initialValues?.optIns, initialValues?.phoneNumber]);

  const searchSummaryFields = useMemo(() => initialValues && (({
    place, minSize, maxSize, minPrice, maxPrice,
  }) => ({
    place, minSize, maxSize, minPrice, maxPrice,
  }))(initialValues), [initialValues]);

  const showSearchSummary = useMemo(() => searchSummaryFields && Object.values(searchSummaryFields).some((value) => !!value),
    [searchSummaryFields]);

  const onSubmit = useCallback(async (formValues: ContactFormModelV3) => {
    setIsSaving(true);
    const source = getTrafficSource(router.query);
    try {
      const optIns: OptInV2[] = [{
        type: OptInType.SERVICE,
        sourceType: SourceType.WEB,
        sourceValue: 'dove.it',
      }];

      if (formValues.marketing) {
        optIns.push({
          type: OptInType.MARKETING,
          sourceType: SourceType.WEB,
          sourceValue: 'dove.it',
        });
      }

      if (formValues.thirdParties) {
        optIns.push({
          type: OptInType.THIRD_PARTIES,
          sourceType: SourceType.WEB,
          sourceValue: 'dove.it',
        });
      }

      const webSearchCriteria: WebSearchCriteria = {
        ...initialValues,
        place: initialValues.place,
        name: formValues.name,
        email: formValues.email,
        phoneNumber: formValues.phoneNumber,
        source,
        optIns,
      };

      const createdWebSearchCriteria = await createWebSearchCriteria(webSearchCriteria);

      setIsSaving(false);
      saveSearchModal.close();
      addSuccess(CREATE_WEB_SEARCH_CRITERIA_SUCCESS_MESSAGE);

      if (onSuccess) {
        onSuccess(createdWebSearchCriteria);
      }
    } catch (err) {
      setIsSaving(false);
      addError(CREATE_WEB_SEARCH_CRITERIA_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, initialValues, onSuccess, router.query, saveSearchModal]);

  return (
    <>
      {showSearchSummary && (
        <Spacing margin={[0, 0, SpacingName.SMALL]}>
          <div
            data-ref={`${dataRef}-modal-summary`}
            css={styles.searchSummary}
          >
            <div
              data-ref={`${dataRef}-modal-place`}
              css={styles.item}
            >
              <Symbol
                path={ICONS.place}
                size={18}
              />
              <Typography.BODY color="neutrals.dark">
                <strong>{searchSummaryFields.place.name}</strong> ({mapTypeToLabel[searchSummaryFields.place.type]})
              </Typography.BODY>
            </div>

            {(searchSummaryFields.minPrice || searchSummaryFields.maxPrice) && (
              <div
                data-ref={`${dataRef}-modal-price`}
                css={styles.item}
              >
                <Symbol
                  path={ICONS.price}
                  size={18}
                />
                <Typography.BODY color="neutrals.dark">
                  <strong>
                    {formatEuroRange(searchSummaryFields.minPrice, searchSummaryFields.maxPrice)}
                  </strong>
                </Typography.BODY>
              </div>
            )}

            {(searchSummaryFields.minSize || searchSummaryFields.maxSize) && (
              <div
                data-ref="search-summary-size"
                css={styles.item}
              >
                <Symbol
                  path={ICONS.size}
                  size={18}
                />
                <Typography.BODY color="neutrals.dark">
                  <strong>
                    {formatSquareMetersRange(searchSummaryFields.minSize, searchSummaryFields.maxSize)}
                  </strong>
                </Typography.BODY>
              </div>
            )}
          </div>
        </Spacing>
      )}

      <Spacing margin={[0, 0, SpacingName.SMALL]}>
        <Typography.BODY>
          <strong>Ti invieremo via mail gli immobili in anteprima</strong>
        </Typography.BODY>
      </Spacing>

      <ContactFormV3
        initialValues={initialFormValues}
        dataRef={`${dataRef}-contact-form`}
        loading={isSaving}
        onSubmit={onSubmit}
      />
    </>
  );
};

export default CreateWebSearchCriteria;
